/**
 * Vintage frames (assets/vintage-frames.css): period objects that hold a
 * photo or video, each picked to fit the medium's shape and type, so a
 * photo shows whole rather than cropped to one shape.
 *
 *   VintageFrames.kindFor({ width, height, type }, seed)
 *     The frame that fits: a square image is a Polaroid; 4:5 and 3:4 a 6x7
 *     film print or a deckle-edged print; 2:3 and taller a deckle print or
 *     a borderless glossy; 4:3 and 5:4 a landscape deckle or 6x7 print;
 *     3:2 and wider a 35mm slide. A video, of any shape, is a length of
 *     Super 8 or camcorder playback. A page (type "page": an element laid
 *     out as a magazine page, such as a product's story) is a page torn
 *     from a 1960s magazine, 3:4 (or taller, down to 1:2, as its width and
 *     height ask).
 *     Where two frames fit, the seed picks one, so a set of photos mixes
 *     them but each photo always gets the same one.
 *
 *   VintageFrames.build({ media, width, height, type, kind, seed })
 *     Builds a frame around `media` (an <img> or <video>, which it moves
 *     into the frame) and returns its parts: { root, body, surface, photo,
 *     glare, wear, shade, shadow, lifted, media, kind, ratio, aspect }.
 *     `kind` is optional (kindFor decides); `width` and `height` are the
 *     medium's own; `seed` (a stable key, such as its URL) fixes the
 *     print's character: cast, curl, dates, frame numbers. `ratio` is the
 *     window's width over height and `aspect` the whole frame's height
 *     over width, so a layout can size it before it renders.
 *
 *   VintageFrames.upgrade(root)
 *     Frames, in place, every [data-vintage-frame] element under `root`
 *     holding an <img> or <video> with width and height attributes. The
 *     attribute's value can name a kind; data-seed fixes the character
 *     (default: the medium's URL). Runs on load and on theme-editor
 *     section loads, so a Liquid section needs no script of its own:
 *
 *       {% render 'vintage-frames-assets' %}
 *       <div data-vintage-frame>{{ image | image_url: width: 720 | image_tag }}</div>
 *
 * The frame takes its container's width. The script loads deferred, so a
 * section's own script that runs earlier can wait for it: it dispatches
 * "vintage-frames:ready" on the document once VintageFrames exists.
 */
(() => {
  if (window.VintageFrames) return;

  /* A small seeded PRNG (FNV-1a hash into mulberry32), so a frame's
   character is tied to its photo rather than reshuffled on every view. */
  const vfRandom = (key) => {
    let seed = Array.from(String(key)).reduce(
      (hash, char) => Math.imul(hash ^ char.charCodeAt(0), 16777619),
      2166136261
    );
    return () => {
      seed = (seed + 0x6d2b79f5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const VF_MONTHS = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC"
  ];

  /* Film casts a print can carry: warm, cool, magenta-faded, neutral. */
  const VF_CASTS = ["255 168 92", "110 160 215", "225 140 175", "235 225 210"];

  const vfClamp = (value, low, high) => Math.min(high, Math.max(low, value));

  // A window within the range of shapes a frame plausibly comes in,
  // portrait or landscape.
  const vfFit = (ratio, [portraitLow, portraitHigh], [wideLow, wideHigh]) =>
    ratio < 1
      ? vfClamp(ratio, portraitLow, portraitHigh)
      : vfClamp(ratio, wideLow, wideHigh);

  /* Each frame's window, from the medium's width over height, and its
   border (top, right, bottom, left) as fractions of the frame's width. */
  const VF_KINDS = {
    polaroid: {
      window: () => 1,
      border: () => [0.058, 0.058, 0.22, 0.058]
    },
    deckle: {
      window: (ratio) => vfFit(ratio, [0.62, 0.8], [1.25, 1.6]),
      border: () => [0.065, 0.065, 0.09, 0.065]
    },
    film: {
      window: (ratio) => vfFit(ratio, [0.75, 0.86], [1.16, 1.34]),
      border: () => [0.075, 0.055, 0.075, 0.055]
    },
    glossy: {
      window: (ratio) => vfFit(ratio, [0.62, 0.72], [1.4, 1.6]),
      border: () => [0, 0, 0, 0]
    },
    // A square mount, whatever the window.
    slide: {
      window: (ratio) => (ratio < 1 ? 2 / 3 : 3 / 2),
      border: () => [0, 0, 0, 0],
      square: true
    },
    camcorder: {
      window: (ratio) => vfClamp(ratio, 0.5, 1.9),
      border: (opening) =>
        opening > 1 ? [0.05, 0.05, 0.05, 0.05] : [0.07, 0.07, 0.07, 0.07]
    },
    super8: {
      // As narrow as a phone's 9:16 video, so it is never cropped.
      window: (ratio) => vfClamp(ratio, 0.56, 1.4),
      border: () => [0.07, 0.045, 0.07, 0.15]
    },
    // The page is the paper: its media is the page's own layout. 3:4 for a
    // magazine page; a taller one (a review card holding a photo) as asked.
    magazine: {
      window: (ratio) => vfClamp(ratio, 0.5, 3 / 4),
      border: () => [0, 0, 0, 0]
    }
  };

  const vfKindFor = ({ width, height, type }, seed = "") => {
    const ratio = width > 0 && height > 0 ? width / height : 1;
    const either = (first, second) =>
      vfRandom(`${seed}#kind`)() < 0.5 ? first : second;
    if (type === "page") return "magazine";
    // Footage either way: a length of Super 8 or a camcorder's playback.
    if (type === "video") return either("super8", "camcorder");
    if (ratio >= 0.93 && ratio <= 1.07) return "polaroid";
    if (ratio > 1.42) return "slide";
    if (ratio > 1.07) return either("deckle", "film");
    if (ratio >= 0.74) return either("film", "deckle");
    return either("deckle", "glossy");
  };

  // The whole frame's height over its width, around a window `opening`
  // wide for each unit high.
  const vfAspectOf = (kind, opening) => {
    const spec = VF_KINDS[kind];
    if (spec.square) return 1;
    const [top, right, bottom, left] = spec.border(opening);
    return top + bottom + (1 - left - right) / opening;
  };

  const vfElement = (className, text) => {
    const node = document.createElement("div");
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const vfSpans = (className, texts) => {
    const row = vfElement(className);
    texts.forEach((text) => {
      const span = document.createElement("span");
      span.textContent = text;
      row.append(span);
    });
    return row;
  };

  /* What each frame carries besides its photo: the embossed month, the
   film's edge lettering, the date-back imprint, the mount's printing, the
   deck's on-screen text, the sprocket hole. Dates are period ones, seeded
   by the photo. */
  const VF_DECOR = {
    deckle: (parts, random) =>
      parts.surface.append(
        vfElement(
          "vframe__date",
          `${VF_MONTHS[Math.floor(random() * 12)]} · ${66 + Math.floor(random() * 14)}`
        )
      ),
    film: (parts, random) => {
      const frame = 1 + Math.floor(random() * 11);
      parts.body.append(
        vfSpans("vframe__edge vframe__edge--top", [
          `◂ ${frame}`,
          "SAFETY FILM · 120",
          `${frame + 1} ▸`
        ]),
        vfSpans("vframe__edge vframe__edge--bottom", ["6×7", `${frame}A`])
      );
    },
    glossy: (parts, random) =>
      parts.surface.append(
        vfElement(
          "vframe__stamp",
          `'${80 + Math.floor(random() * 9)}  ${1 + Math.floor(random() * 12)}  ${1 + Math.floor(random() * 28)}`
        )
      ),
    slide: (parts, random) =>
      parts.body.append(
        vfElement("vframe__label vframe__label--top", "TRANSPARENCY"),
        vfElement(
          "vframe__label vframe__label--bottom",
          `PROCESSED  ${VF_MONTHS[Math.floor(random() * 12)]} 19${68 + Math.floor(random() * 12)}`
        ),
        vfElement(
          "vframe__number",
          String(2 + Math.floor(random() * 34)).padStart(2, "0")
        )
      ),
    camcorder: (parts, random) => {
      const osd = vfSpans("vframe__osd", [
        "▶ PLAY",
        "SP",
        `${VF_MONTHS[Math.floor(random() * 12)]} ${1 + Math.floor(random() * 28)} 19${84 + Math.floor(random() * 8)}`,
        "0:00:00"
      ]);
      const timecode = osd.lastElementChild;
      // The deck's counter runs with the clip.
      if (parts.media.tagName === "VIDEO")
        parts.media.addEventListener("timeupdate", () => {
          const seconds = Math.floor(parts.media.currentTime);
          timecode.textContent = `0:${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
        });
      parts.photo.append(osd);
    },
    super8: (parts) => {
      parts.body.append(vfElement("vframe__hole"));
      // The gate weaves and the film flickers only while it runs: a short
      // animation left running wakes the page several times a second, and
      // every floating print is restyled with it.
      // A still (an <img>, which has no `paused`) never runs.
      const running = () =>
        parts.root.classList.toggle("is-playing", parts.media.paused === false);
      ["playing", "pause", "ended", "emptied"].forEach((type) =>
        parts.media.addEventListener(type, running)
      );
      running();
    }
  };

  const vfPercent = (fraction) => `${(fraction * 100).toFixed(3)}%`;

  const vfBuild = ({ media, width, height, type, kind, seed = "" }) => {
    const ratio = width > 0 && height > 0 ? width / height : 1;
    const name = VF_KINDS[kind]
      ? kind
      : vfKindFor({ width, height, type }, seed);
    const spec = VF_KINDS[name];
    const opening = spec.window(ratio);
    const [top, right, bottom, left] = spec.border(opening);
    const aspect = vfAspectOf(name, opening);
    const random = vfRandom(seed);
    const between = (min, max) => min + random() * (max - min);
    const root = vfElement(`vframe vframe--${name}`);
    root.classList.toggle("is-portrait", opening < 1);
    root.classList.toggle("is-wide", opening > 1);
    // The geometry the stylesheet lays the frame out by, and the print's
    // own character: how it curls, its developer's edge, its film's cast,
    // fade and vignette.
    const values = {
      "--vf-ratio": opening.toFixed(4),
      "--vf-pt": vfPercent(top),
      "--vf-pr": vfPercent(right),
      "--vf-pb": vfPercent(bottom),
      "--vf-pl": vfPercent(left),
      "--vf-edge-top": vfPercent(top / 2 / aspect),
      "--vf-edge-bottom": vfPercent(bottom / 2 / aspect),
      "--bend": between(0.25, 1).toFixed(2),
      "--bend-a": `${between(0, 360).toFixed(1)}deg`,
      "--chem-x": `${between(10, 70).toFixed(1)}%`,
      "--chem2-x": `${between(40, 90).toFixed(1)}%`,
      "--cast": VF_CASTS[Math.floor(random() * VF_CASTS.length)],
      "--cast-a": between(0.02, 0.07).toFixed(3),
      "--fade": between(0.02, 0.08).toFixed(3),
      "--vignette": between(0.14, 0.32).toFixed(3)
    };
    Object.entries(values).forEach(([property, value]) =>
      root.style.setProperty(property, value)
    );
    const shadow = vfElement("vframe__shadow");
    const lifted = vfElement("vframe__lifted");
    const body = vfElement("vframe__body");
    // The deckle print's edge is cut on an inner layer (see the stylesheet).
    const surface = name === "deckle" ? vfElement("vframe__cut") : body;
    if (surface !== body) body.append(surface);
    const photo = vfElement("vframe__photo");
    const glare = vfElement("vframe__glare");
    media.classList.add("vframe__media");
    photo.append(media, glare);
    const wear = vfElement("vframe__wear");
    const shade = vfElement("vframe__shade");
    surface.append(photo, wear, shade);
    root.append(shadow, lifted, body);
    const parts = {
      root,
      body,
      surface,
      photo,
      glare,
      wear,
      shade,
      shadow,
      lifted,
      media,
      kind: name,
      ratio: opening,
      aspect
    };
    if (VF_DECOR[name]) VF_DECOR[name](parts, random);
    return parts;
  };

  const vfUpgrade = (root = document) => {
    root.querySelectorAll("[data-vintage-frame]").forEach((host) => {
      if (host.vintageFrame) return;
      const media = host.querySelector("img, video");
      if (!media) return;
      const width =
        Number(media.getAttribute("width")) ||
        media.naturalWidth ||
        media.videoWidth;
      const height =
        Number(media.getAttribute("height")) ||
        media.naturalHeight ||
        media.videoHeight;
      host.vintageFrame = vfBuild({
        media,
        width,
        height,
        type: media.tagName === "VIDEO" ? "video" : "image",
        kind: host.dataset.vintageFrame,
        seed: host.dataset.seed || media.currentSrc || media.src
      });
      host.append(host.vintageFrame.root);
    });
  };

  window.VintageFrames = {
    kindFor: vfKindFor,
    build: vfBuild,
    aspectOf: vfAspectOf,
    upgrade: vfUpgrade
  };
  document.dispatchEvent(new CustomEvent("vintage-frames:ready"));

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => vfUpgrade());
  else vfUpgrade();
  document.addEventListener("shopify:section:load", (event) =>
    vfUpgrade(event.target)
  );
})();
